import { useCallback, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import { DetailedLoadStatus } from "../utils/async-status";

enum ActionType {
  ADD_HANDLER,
  CLEAR_HANDLERS,
}

function reducer(state, action) {
  switch (action.type) {
    case ActionType.ADD_HANDLER:
      return [...state, action.handler];
    case ActionType.CLEAR_HANDLERS:
      return [];
    default:
      return state;
  }
}

/**
 * You probably don't want this!
 *
 * Create a getter function that resolves when the data in redux is in
 * some sort or ready state. Useful to tie an external API to the store,
 * but most standard stuff will use typical actions and selectors.
 */
export function useReduxGetter<T>(
  dataSelector,
  loadStatusSelector
  // Could add initiating action here
) {
  const data = useSelector<any, T>(dataSelector);
  const loadStatus = useSelector<any, DetailedLoadStatus>(loadStatusSelector);
  const [handlers, localDispatch] = useReducer(reducer, []);

  useEffect(() => {
    if (
      handlers.length === 0 ||
      loadStatus === DetailedLoadStatus.WAITING ||
      loadStatus === DetailedLoadStatus.FETCHING
    ) {
      return;
    }

    if (loadStatus === DetailedLoadStatus.OK) {
      handlers.forEach((handler) => handler.resolve(data));
    } else if (loadStatus === DetailedLoadStatus.ERROR) {
      handlers.forEach((handler) => handler.reject());
    }
    localDispatch({ type: ActionType.CLEAR_HANDLERS });
  }, [data, loadStatus, handlers]);

  return useCallback(() => {
    return new Promise<T>((resolve, reject) => {
      localDispatch({
        type: ActionType.ADD_HANDLER,
        handler: { resolve, reject },
      });
    });
  }, []);
}
